import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanySupportChatQuery } from 'interfaceAdapter/queries/CompanySupportChat';
import { CompanySupportChatRepository } from 'interfaceAdapter/repositories/CompanySupportChat';
import { useLoading } from 'application/contexts/useLoading';
import { IChat } from 'domain/entities/Chat/Chat';

type CompanySupportChatContext = {
    loading: boolean;
    chatMessages: IChat[];
    message: string;
    setMessage: (message: string) => void;
    sendMessage: () => void;
    fetchData: () => void;
};

const defaultContext: CompanySupportChatContext = {
    loading: false,
    chatMessages: [],
    message: '',
    setMessage: () => {},
    sendMessage: () => {},
    fetchData: () => {},
};

export const companySupportChatContext = createContext<CompanySupportChatContext>(defaultContext);

export const useCompanySupportChat = (): CompanySupportChatContext => {
    const diContainer = useContext(diContainerContext);
    const companySupportChatQuery = diContainer.resolve(CompanySupportChatQuery);
    const companySupportChatRepository = diContainer.resolve(CompanySupportChatRepository);
    const { loading, setLoading } = useLoading();
    const [chatMessages, setChatMessages] = useState<IChat[]>([]);
    const [message, setMessage] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await companySupportChatQuery.fetchMessages();
            setChatMessages(data);
        } catch (error) {
            console.error('サポートチャットメッセージの取得に失敗しました:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // チャットメッセージ送信
    const sendMessage = useCallback(async () => {
        if (!message.trim()) return;
        setLoading(true);
        try {
            await companySupportChatRepository.send(message);
            setMessage('');
            await fetchData(); // メッセージ一覧を更新
        } catch (error) {
            console.error('メッセージ送信エラー:', error);
        } finally {
            setLoading(false);
        }
    }, [message, companySupportChatRepository, fetchData, setLoading]);

    return {
        loading,
        chatMessages,
        message,
        setMessage,
        sendMessage,
        fetchData,
    };
};

